import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BuilderProfileState } from '@/modules/__generated__/graphql/switchboard-generated';
import { BUILDERS_TABLE_COLUMNS, type BuildersTableColumn } from '@/modules/builders/lib/constants';
import { useMediaQuery } from '@/modules/shared/hooks/use-media-query';
import { useBuildersFiltersContext } from '../../../builders-filters/builders-filters-context';
import type SimpleBar from 'simplebar-react';
interface UseBuildersTableProps {
  builders: BuilderProfileState[];
  asSectionContent?: boolean;
}
export enum SortEnum {
  Neutral = 'neutral',
  Asc = 'asc',
  Desc = 'desc',
  Disabled = 'disabled',
}
export function useBuildersTable(t0) {
  const $ = _c(23);
  const {
    builders,
    asSectionContent: t1
  } = t0;
  const asSectionContent = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = BUILDERS_TABLE_COLUMNS.map(_temp);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [headersSort, setHeadersSort] = useState(t2);
  const [sortColumn, setSortColumn] = useState(-1);
  const {
    isResetPending
  } = useBuildersFiltersContext();
  const simpleBarRef = useRef(null);
  const cardContentRef = useRef(null);
  const itemsWrapperRef = useRef(null);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      from: "lg"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const isDesktop = useMediaQuery(t3);
  let t4;
  if ($[2] !== isDesktop) {
    t4 = BUILDERS_TABLE_COLUMNS.map(column_0 => ({
      accessorKey: column_0.accessorKey,
      hasSort: column_0.hasSort,
      sortReverse: column_0.sortReverse,
      isNumeric: column_0.isNumeric,
      header: !isDesktop && column_0.shortHeader ? column_0.shortHeader : column_0.header
    }));
    $[2] = isDesktop;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const proccesedBuildersTableColumns = t4;
  let t5;
  if ($[4] !== headersSort) {
    t5 = index => {
      const sortNeutralState = BUILDERS_TABLE_COLUMNS.map(_temp2) as SortEnum[];
      if (headersSort[index] === SortEnum.Neutral) {
        if (BUILDERS_TABLE_COLUMNS[index].sortReverse) {
          sortNeutralState[index] = SortEnum.Desc;
        } else {
          sortNeutralState[index] = SortEnum.Asc;
        }
      } else {
        sortNeutralState[index] = headersSort[index] === SortEnum.Asc ? SortEnum.Desc : SortEnum.Asc;
      }
      setHeadersSort(sortNeutralState);
      setSortColumn(index);
    };
    $[4] = headersSort;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleSortClick = t5;
  let t6;
  if ($[6] !== headersSort || $[7] !== sortColumn) {
    t6 = builders_0 => {
      if (sortColumn === -1) {
        return builders_0;
      }
      const column_2 = BUILDERS_TABLE_COLUMNS[sortColumn];
      const sortDirection = headersSort[sortColumn];
      const effectiveSortDirection = column_2.sortReverse && sortDirection !== SortEnum.Neutral && sortDirection !== SortEnum.Disabled ? sortDirection === SortEnum.Asc ? SortEnum.Desc : SortEnum.Asc : sortDirection;
      if (sortDirection === SortEnum.Neutral || sortDirection === SortEnum.Disabled) {
        return builders_0;
      }
      return [...builders_0].sort((a, b) => {
        const getSortableValue = builder => {
          if (column_2.accessorKey === "lastModified") {
            if (!builder.lastModified) {
              return -1;
            }
            const timestamp = new Date(builder.lastModified).getTime();
            return Number.isNaN(timestamp) ? -1 : timestamp;
          }
          const value = builder[column_2.accessorKey as keyof BuilderProfileState];
          if (value === null) {
            return "";
          }
          if (typeof value === "string") {
            return value.toLowerCase();
          }
          if (typeof value === "number") {
            return value;
          }
          if (Array.isArray(value)) {
            return value.length;
          }
          if (value instanceof Date) {
            return value.getTime();
          }
          return "";
        };
        const aValue = getSortableValue(a);
        const bValue = getSortableValue(b);
        if (aValue < bValue) {
          return effectiveSortDirection === SortEnum.Asc ? -1 : 1;
        }
        if (aValue > bValue) {
          return effectiveSortDirection === SortEnum.Asc ? 1 : -1;
        }
        return 0;
      });
    };
    $[6] = headersSort;
    $[7] = sortColumn;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const sortBuilders = t6;
  let t7;
  if ($[9] !== builders || $[10] !== sortBuilders) {
    t7 = sortBuilders(builders);
    $[9] = builders;
    $[10] = sortBuilders;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const sortedBuilders = t7;
  let t8;
  let t9;
  if ($[12] !== asSectionContent || $[13] !== builders.length || $[14] !== isDesktop) {
    t8 = () => {
      if (!asSectionContent) {
        return;
      }
      const simpleBarEl = simpleBarRef.current?.el;
      const cardContentEl = cardContentRef.current;
      const itemsWrapperEl = itemsWrapperRef.current;
      if (!simpleBarEl || !cardContentEl || !itemsWrapperEl) {
        return;
      }
      const teamsCount = builders.length;
      let config = null;
      if (isDesktop) {
        config = {
          maxHeight: "630px",
          maxItems: 7
        };
      }
      if (!config) {
        simpleBarEl.style.maxHeight = "";
        cardContentEl.style.paddingRight = "";
        itemsWrapperEl.style.paddingRight = "";
        itemsWrapperEl.style.paddingLeft = "";
        itemsWrapperEl.style.paddingBottom = "";
        return;
      }
      if (teamsCount > config.maxItems) {
        simpleBarEl.style.maxHeight = config.maxHeight;
        cardContentEl.style.paddingRight = "4px";
        itemsWrapperEl.style.paddingRight = "12px";
      }
    };
    t9 = [isDesktop, builders.length, asSectionContent];
    $[12] = asSectionContent;
    $[13] = builders.length;
    $[14] = isDesktop;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  if ($[17] !== handleSortClick || $[18] !== headersSort || $[19] !== isResetPending || $[20] !== proccesedBuildersTableColumns || $[21] !== sortedBuilders) {
    t10 = {
      proccesedBuildersTableColumns,
      headersSort,
      sortedBuilders,
      isResetPending,
      simpleBarRef,
      cardContentRef,
      itemsWrapperRef,
      handleSortClick
    };
    $[17] = handleSortClick;
    $[18] = headersSort;
    $[19] = isResetPending;
    $[20] = proccesedBuildersTableColumns;
    $[21] = sortedBuilders;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp2(column_1) {
  return column_1.hasSort ? SortEnum.Neutral : SortEnum.Disabled;
}
function _temp(column) {
  return column.hasSort ? SortEnum.Neutral : SortEnum.Disabled;
}